import type { ProductInfoDto, CloudProductInfoDto } from '@/castapi';
import { PURCHASE_OPTIONS_IDS } from '@/utils';

export const getProductCode = (product: ProductInfoDto | CloudProductInfoDto): string => {
  return (product as ProductInfoDto).serialCode || (product as CloudProductInfoDto).licenseCode;
};

/**
 * Customer-facing name for a dongle — "WYSIWYG Perform", "WYSIWYG Design + Vivien", "Vivien".
 *
 * Deliberately built from the product level and the Vivien add-on only. `productType` is the
 * purchase-option name ("PERPETUAL", "UPGRADE STUDENT TO PERPETUAL WITH MEMBERSHIP"), which says how
 * the dongle was last transacted rather than what it is, and is empty whenever the dongle has no
 * product link. Sub-product-line 24 is seeded lowercase as 'none'.
 */
export const getDongleProductName = (dongle: ProductInfoDto): string => {
  const hasWysiwyg = Boolean(dongle?.productLevel) && dongle.productLevel !== 'none';
  const hasVivien = Boolean(dongle?.vivLevel) && dongle.vivLevel !== 'none';
  if (hasWysiwyg && hasVivien) {
    return `WYSIWYG ${dongle.productLevel} + Vivien`;
  }
  if (hasWysiwyg) {
    return `WYSIWYG ${dongle.productLevel}`;
  }
  return hasVivien ? 'Vivien' : 'Dongle';
};

export const isAutoRenewAvailableForPurchaseOption = (purchaseOptionId: number): boolean => {
  const cloudPurchaseOptions = [
    PURCHASE_OPTIONS_IDS.CLOUD_LICENSE_ID,
    PURCHASE_OPTIONS_IDS.CLOUD_LICENSE_RENEW_ID,
    PURCHASE_OPTIONS_IDS.CLOUD_LICENSE_UPGRADE_ID,
  ];
  const dongleRenewalOptions = [
    PURCHASE_OPTIONS_IDS.MEMBERSHIP_RENEW_ID,
    PURCHASE_OPTIONS_IDS.LEASE_RENEW_ID,
    PURCHASE_OPTIONS_IDS.UPGRADE_LEASE_ID,
    PURCHASE_OPTIONS_IDS.UPGRADE_PERPETUAL_ID,
    PURCHASE_OPTIONS_IDS.UPGRADE_LEASE_TO_PERPETUAL_ID,
    PURCHASE_OPTIONS_IDS.UPGRADE_LEASE_TO_PERPETUAL_WITH_MEMBERSHIP_ID,
    PURCHASE_OPTIONS_IDS.DOWNGRADE_PERPETUAL_TO_LEASE_ID,
  ];
  return cloudPurchaseOptions.includes(purchaseOptionId) || dongleRenewalOptions.includes(purchaseOptionId);
};
